import { useAsyncData, useCookie, useNuxtApp, useUniversalFetch, useUniversalPost } from "#imports";
import { FetchError } from "ofetch";
import { defineStore } from "pinia";
import { computed, getCurrentInstance, ref, watch } from "vue";
import { isLocalStorageEnabled, isSessionStorageEnabled, safelyNavigateTo } from "@malt/utils";
export const useAuth = defineStore("auth", () => {
  const selectedIdentityCookie = useCookie("hopsi", { path: "/" });
  const pathOfWorkflowInProgressCookie = useCookie("path-of-mandatory-workflow-likely-in-progress");
  const user = ref(null);
  const loggedIn = ref(!!user.value);
  const userLocale = ref();
  const userVisitorId = ref();
  const hasAlreadyCheckIfLogged = ref(false);
  const hasAlreadyCurrentSession = ref(false);
  const isUmbrellaCompany = computed(() => user.value?.extraInformation?.umbrellaCompany === "true");
  const getSelectedIdentity = computed(() => {
    return user.value?.identities?.find((identity) => identity.selected);
  });
  const currentIdentityIsClient = computed(() => {
    const selectedIdentity = getSelectedIdentity.value;
    return selectedIdentity?.identityType?.toLowerCase() === "client";
  });
  const currentIdentityIsFreelance = computed(() => {
    const selectedIdentity = getSelectedIdentity.value;
    return selectedIdentity?.identityType?.toLowerCase() === "freelance";
  });
  const currentIdentityIsUndefined = computed(() => {
    const selectedIdentity = getSelectedIdentity.value;
    return selectedIdentity?.identityType?.toLowerCase() === "undefined";
  });
  const hasProfile = computed(() => {
    return user.value?.identities?.find((identity) => identity.identityType.toLowerCase() === "freelance") != null;
  });
  const isCompanySuspended = computed(() => user.value?.extraInformation?.companySuspended === "true");
  const isFreelancerApplicationInProgress = computed(() => user.value?.extraInformation?.freelancerApplicationInProgress === "true");
  const organizationMemberDetails = computed(() => user.value?.organizationMemberDetails);
  watch(user, updateSelectedIdentityCookie);
  function updateSelectedIdentityCookie() {
    if (user.value && (import.meta.server || document.hasFocus())) {
      if (selectedIdentityCookie.value !== user.value.selectedIdentity) {
        selectedIdentityCookie.value = user.value.selectedIdentity;
      }
    }
  }
  async function login(username, password) {
    const response = await useUniversalPost("/api/user/signin", {
      body: {
        username,
        password
      }
    });
    loggedIn.value = true;
    user.value = response;
  }
  async function logout(redirectUrl = "/signin", options = { externalToThisApp: true }) {
    await useUniversalPost("/api/logout");
    if (isSessionStorageEnabled()) {
      sessionStorage.clear();
    }
    if (isLocalStorageEnabled()) {
      localStorage.clear();
    }
    await safelyNavigateTo(redirectUrl, options);
  }
  function hasEnoughPrivilege(requiredRoles) {
    if (Array.isArray(requiredRoles)) {
      const noPrivilegeNeeded = requiredRoles.length === 0;
      const hasAllRequiredRoles = requiredRoles.every((role) => (user.value?.roles || []).includes(role));
      return noPrivilegeNeeded || hasAllRequiredRoles;
    } else {
      if (requiredRoles.or.length === 0) {
        throw new Error('"requiredRoles.or" must not be empty');
      }
      return requiredRoles.or.some((subConfig) => hasEnoughPrivilege(subConfig));
    }
  }
  function hasCorrectIdentity(requiredIdentity) {
    if (requiredIdentity === "ANY") {
      return true;
    } else {
      const selectedIdentity = user.value?.identities?.find((identity) => identity.id === user.value?.selectedIdentity);
      if (selectedIdentity) {
        return selectedIdentity.identityType === requiredIdentity;
      }
      return false;
    }
  }
  async function getImmersionOwner(request) {
    let userInImmersion;
    try {
      if (request.type === "IDENTITY_ID") {
        userInImmersion = await useUniversalFetch(`/api/user/identity/${request.id}`);
      } else if (request.type === "ACCOUNT_ID") {
        userInImmersion = await useUniversalFetch(`/api/user/account/${request.id}`);
      }
    } catch (error) {
      console.error(error);
    }
    return userInImmersion;
  }
  async function switchIdentity(identity) {
    const identityIdBeforeEnforcement = user.value?.selectedIdentity;
    const response = await useUniversalPost(`/api/user/signin/identity?identityId=${identity}`);
    user.value = response;
    if (user.value.selectedIdentity !== identityIdBeforeEnforcement) {
      advertiseEnforcedIdentity(user.value.selectedIdentity);
    }
  }
  function isThisIdentityInImmersionMode(identityId) {
    if (!identityId) {
      return false;
    }
    if (!user.value) {
      throw new Error("isThisIdentityInImmersionMode can't be called when auth store is empty");
    }
    return !!(user.value.selectedIdentity !== identityId && user.value.roles?.includes("ROLE_ADMIN"));
  }
  async function impersonate(accountId) {
    const response = await useUniversalPost(`/api/user/sudosu?accountId=${accountId}`);
    user.value = response;
  }
  async function unsudosu() {
    const response = await useUniversalFetch(`/api/user/unsudosu`, { method: "POST" });
    user.value = response;
  }
  async function refresh() {
    try {
      const response = await useUniversalFetch("/api/user/me");
      user.value = response;
      loggedIn.value = true;
    } catch (error) {
      if (!(error instanceof FetchError) || error.status !== 401 && error.status !== 403) {
        console.error("Unable to refresh current user", error);
      }
      user.value = null;
      loggedIn.value = false;
    }
  }
  async function isLogged() {
    if (!loggedIn.value) {
      try {
        user.value = await useUniversalFetch("/api/user/me");
        loggedIn.value = true;
      } catch (error) {
        if (!(error instanceof FetchError) || error.status !== 401 && error.status !== 403) {
          console.error("No user found", error);
        }
      }
    }
    return loggedIn.value;
  }
  async function loginOnce() {
    if (!loggedIn.value && !hasAlreadyCheckIfLogged.value) {
      await isLogged();
      hasAlreadyCheckIfLogged.value = true;
    }
    return loggedIn.value;
  }
  async function checkIfLoggedOrIfNeedingRedirect() {
    if (!hasAlreadyCurrentSession.value) {
      const url = "/api/user/current-session";
      const { data, error } = await useAsyncData("current-user-session", async () => {
        hasAlreadyCurrentSession.value = true;
        return await useUniversalFetch(url);
      });
      if (error.value) {
        console.error("No user found", error.value);
        return {
          isLogged: loggedIn.value
        };
      }
      const { loggedInUser, pathWhereToRedirectUser } = data.value;
      user.value = loggedInUser || null;
      loggedIn.value = user.value != null;
      pathOfWorkflowInProgressCookie.value = pathWhereToRedirectUser || null;
      if (data.value) {
        const { visitorId, locale } = data.value;
        if (locale) {
          userLocale.value = locale;
        }
        if (visitorId) {
          userVisitorId.value = visitorId;
        }
        if (!visitorId || !locale) {
          console.error("Unable to get visitorId or locale while fetching current session, got:", data.value);
        }
      }
    }
    return {
      isLogged: loggedIn.value,
      pathWhereToRedirectUser: pathOfWorkflowInProgressCookie.value || void 0
    };
  }
  async function tryToEnforceIdentity(enforcementRequest, allowAdminIfNoEnforcedIdentity) {
    const identityIdBeforeEnforcement = user.value?.selectedIdentity;
    const url = `/api/user/identity-enforcement-request?v2${allowAdminIfNoEnforcedIdentity !== void 0 ? `&allowAdminIfNoEnforcedIdentity=${allowAdminIfNoEnforcedIdentity}` : ""}`;
    let data = ref();
    let error = ref();
    const nuxtApp = useNuxtApp();
    const instance = getCurrentInstance();
    const isMountedAppSession = !nuxtApp.isHydrating && (!instance || instance?.isMounted) && import.meta.client;
    if (isMountedAppSession) {
      try {
        const fetchData = await useUniversalFetch(url, {
          method: "POST",
          body: enforcementRequest
        });
        data.value = fetchData;
      } catch (e) {
        error.value = e;
      }
    } else {
      ({ data, error } = await useAsyncData(
        "identity-enforcement-request-v2",
        () => useUniversalFetch(url, {
          method: "POST",
          body: enforcementRequest
        })
      ));
    }
    if (error.value) {
      if (error.value instanceof FetchError) {
        console.error("Unexpected error when enforcing identity", error);
      }
      return {
        isLogged: loggedIn.value,
        hasValidIdentity: true
      };
    }
    const enforcementResponse = data.value;
    pathOfWorkflowInProgressCookie.value = enforcementResponse.currentSession.pathWhereToRedirectUser || null;
    const loggedInUser = enforcementResponse.currentSession.loggedInUser;
    if (loggedInUser) {
      user.value = loggedInUser;
      loggedIn.value = true;
      if (user.value.selectedIdentity !== identityIdBeforeEnforcement) {
        advertiseEnforcedIdentity(user.value.selectedIdentity);
      }
      return {
        isLogged: true,
        hasValidIdentity: enforcementResponse.identitySuccessfullyEnforced,
        pathWhereToRedirectUser: enforcementResponse.currentSession.pathWhereToRedirectUser
      };
    } else {
      user.value = null;
      loggedIn.value = false;
      return {
        isLogged: false,
        hasValidIdentity: false,
        pathWhereToRedirectUser: enforcementResponse.currentSession.pathWhereToRedirectUser
      };
    }
  }
  function getOrganizationMemberDetails(organizationId) {
    return organizationMemberDetails.value?.find((organizationMemberDetails2) => organizationMemberDetails2?.organizationId === organizationId);
  }
  function advertiseEnforcedIdentity(enforcedIdentityId) {
    if (import.meta.server) {
      return;
    }
    window.dispatchEvent(
      new CustomEvent("user-identity-enforced", {
        detail: { enforcedIdentityId }
      })
    );
  }
  function currentIdentityHasAccessTo(productFeature) {
    return getSelectedIdentity.value?.features.includes(productFeature) || false;
  }
  return {
    // A-Z refs
    currentIdentityIsClient,
    currentIdentityIsFreelance,
    currentIdentityIsUndefined,
    getSelectedIdentity,
    hasAlreadyCheckIfLogged,
    hasAlreadyCurrentSession,
    hasProfile,
    isCompanySuspended,
    isFreelancerApplicationInProgress,
    isUmbrellaCompany,
    loggedIn,
    user,
    userLocale,
    userVisitorId,
    // A-Z functions
    checkIfLoggedOrIfNeedingRedirect,
    currentIdentityHasAccessTo,
    getImmersionOwner,
    getOrganizationMemberDetails,
    hasCorrectIdentity,
    hasEnoughPrivilege,
    impersonate,
    isLogged,
    isThisIdentityInImmersionMode,
    login,
    loginOnce,
    logout,
    refresh,
    switchIdentity,
    tryToEnforceIdentity,
    unsudosu,
    updateSelectedIdentityCookie
  };
});
